// MaterialDocument — the editor shell/body, pure and GENERIC.
//
// The material is the protagonist (docs/design README): this is the document
// body between the toolbar and the composer (.claude/research/design/02 centre zone). It
// renders the typed element tree in document register — the reading face, the
// comfortable measure, real vertical rhythm — and owns NO element kind and NO
// edit state. Every element's body comes from the injected renderer registry
// (the O(1) generic shell over O(n) per-kind renderers); every proposal,
// provenance, marker and diagnostic arrives as props; every interaction reports
// UP. This is the "editor renders off deps/props; the shell owns impurity" rule
// (project_editor_is_material_surface) at the top of the tree.
//
// Per-element annotations (who authored it, any pending proposal, any gutter
// wear) arrive keyed by ElementId in one `annotations` map, defaulting to
// human-authored/clean when absent — so the common case (untouched element) is
// zero config. Interaction state (which review is open, which element shows the
// over-trust cue) is also injected and reported up, never held here; the demo
// wiring lives in story useState.
//
// The dispatch to renderers is a TOTAL switch that narrows the element to its
// exact variant before calling the matching registry entry — no cast, and a new
// Element kind breaks compilation here until it is handled.

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import {
    type ClipboardEvent,
    Fragment,
    type ReactNode,
    type RefObject,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { CreateMenu } from "../../CreateMenu/CreateMenu.js";
import type { FocusTarget } from "../../Navigation/types.js";
import { PopoverAnchor } from "../../Popover/Popover.js";
import { useLandingMarks } from "../../StudioLanding/landingMarks.js";
import { type AskLeave, AskLine } from "../AskLine/AskLine.js";
import { ElementBlock } from "../ElementBlock/ElementBlock.js";
import type { RuleEffect, SlashTriggerState } from "../inputRules.js";
import { menuRowOf, type SlashItem } from "../SlashMenu/slashItems.js";
import {
    elementsToMarkdown,
    wholeBlockMarkdown,
} from "../serializeMarkdown.js";
import { type TextKind, textKindOf, turnIntoOf } from "../textKind.js";
import type {
    Element,
    ElementId,
    ElementRenderers,
    GutterMarker,
    HeadingElement,
    KeptLine,
    PartElement,
    PendingProposal,
    Provenance,
    QuestionEnd,
    ReviewOutcome,
} from "../types.js";
import { elementRuns } from "./elementRuns.js";
import {
    MENU_GAP,
    type MenuPlace,
    placeMenu,
    slashPointIn,
} from "./slashPlace.js";
import "./MaterialDocument.css";
import { useRevealTarget } from "./revealElement.js";

// Walk UP from a DOM node to the nearest `[data-element-id]` ancestor — the
// per-block wrapper div below carries one for exactly this purpose. Returns
// `undefined` past the document root (a click/selection outside any block).
// The copy/cut handler below uses this for two DOM reads: the block's id
// (which block(s) does the selection touch) and, for a single-block
// selection, the block's own `[data-block-body]` descendant's `textContent`
// (does the selection cover the WHOLE block, per `wholeBlockMarkdown`'s
// header note) — it never inspects anything else about the node (no
// per-character offsets, no Range internals).
const nearestBlockElement = (node: Node | null): HTMLElement | undefined => {
    let current: HTMLElement | null =
        node instanceof HTMLElement ? node : (node?.parentElement ?? null);
    while (current !== null) {
        if (current.dataset.elementId !== undefined) return current;
        current = current.parentElement;
    }
    return undefined;
};

// The `/` menu (the one Create menu, CreateMenu.tsx), fully controlled by the
// caller — like every other interaction slot on this shell, MaterialDocument
// holds NO slash-menu state of its own (see the file header: proposals,
// provenance, wear are all injected; this is that same discipline applied to
// the newest affordance). `forElementId` names the block it is at;
// MaterialDocument renders it only when that id is among `elements`:
//  - open: the rows, floating just under the block;
//  - held: a row the surface around the editor owns opened its next step
//    (the Studio's Create from a source); the block holds only the anchor
//    that step's popover (Popover.tsx's AnchoredPopover) sits at;
//  - asking: the Studio's Ask here was picked, and the block is the line the
//    request is written in (AskLine.tsx, pen Q66 scene B).
//
// SHORTCUT (stated per the lifecycle-design principle, not hidden): the menu
// sits under the named block, not at the live text caret's pixel position.
// That position is a DOM fact only the block's own text input can supply —
// that input (EditableText, a plain `<textarea>`) has no caret-tracking today
// (see inputRules.ts's file header for the fuller note).
export type SlashMenuState =
    | {
          readonly type: "open";
          readonly forElementId: ElementId;
          readonly items: readonly SlashItem[];
          readonly activeIndex: number;
          readonly onCommit: (item: SlashItem) => void;
          readonly onHoverIndex: (index: number) => void;
          // Offline: the rows show but pick nothing.
          readonly disabled: boolean;
          // The active row asks with the words typed (the foot says how).
          readonly asking: boolean;
      }
    | { readonly type: "held"; readonly forElementId: ElementId }
    | {
          readonly type: "asking";
          readonly forElementId: ElementId;
          readonly words: string;
          readonly online: boolean;
          readonly max: number;
          readonly onAsk: (words: string) => void;
          readonly onLeave: (words: string, how: AskLeave) => void;
      };

// Where the "/" menu (or the anchor of its step) sits in its block (pen Q73,
// slashPlace.ts), measured after each render that moves it; until measured,
// under the block at its left edge. View-ephemeral: a pixel place only.
const useSlashPlace = (
    key: string,
): {
    readonly ref: RefObject<HTMLDivElement>;
    readonly place: MenuPlace | undefined;
} => {
    const ref = useRef<HTMLDivElement>(null);
    const [place, setPlace] = useState<MenuPlace | undefined>(undefined);
    // biome-ignore lint/correctness/useExhaustiveDependencies: `key` (the block and the words typed) is what moves the "/".
    useLayoutEffect(() => {
        const slot = ref.current;
        const block = slot?.closest<HTMLElement>("[data-element-id]");
        const page = slot?.closest<HTMLElement>(".bilda-material-document");
        const point = block == null ? undefined : slashPointIn(block);
        if (slot == null || block == null || page == null) return;
        if (point === undefined) return setPlace(undefined);
        const menu = slot.getBoundingClientRect();
        const next = placeMenu({
            point,
            parent: block.getBoundingClientRect(),
            page: page.getBoundingClientRect(),
            menu: { width: menu.width, height: menu.height },
            viewportHeight: window.innerHeight,
        });
        setPlace((was) =>
            was?.left === next.left && was?.top === next.top ? was : next,
        );
    }, [key]);
    return { ref, place };
};

// The `/` menu at the "/" in its block (open) or the anchor of the step it
// opened (held), placed by `useSlashPlace`.
const SlashSlot = ({
    state,
}: {
    readonly state: SlashMenuState;
}): ReactNode => {
    switch (state.type) {
        case "held":
            return <HeldAnchor at={state.forElementId} />;
        case "asking":
            return null;
        case "open":
            return <OpenSlash state={state} />;
        default: {
            const exhaustive: never = state;
            return exhaustive;
        }
    }
};

// The anchor a held step's popover sits at: a point at the "/" (Radix
// opens it 6 px below, flipped and kept in the viewport on collision).
const HeldAnchor = ({ at }: { readonly at: ElementId }): ReactNode => {
    const { ref, place } = useSlashPlace(`held:${at}`);
    return (
        <div
            ref={ref}
            className="bilda-material-document__held"
            style={
                place === undefined
                    ? undefined
                    : { left: place.left, top: place.top - MENU_GAP }
            }
        >
            <PopoverAnchor />
        </div>
    );
};

const OpenSlash = ({
    state,
}: {
    readonly state: Extract<SlashMenuState, { readonly type: "open" }>;
}): ReactNode => {
    const { t } = useLingui();
    const { ref, place } = useSlashPlace(
        `${state.forElementId}:${state.items.length}`,
    );
    return (
        <div
            ref={ref}
            className="bilda-material-document__slash"
            style={
                place === undefined
                    ? undefined
                    : { left: place.left, top: place.top }
            }
        >
            <CreateMenu
                label={t`Create`}
                listboxId={`bilda-slash-${state.forElementId}`}
                rows={state.items.map(menuRowOf)}
                activeIndex={state.activeIndex}
                disabled={state.disabled}
                onPick={(index) => {
                    const item = state.items[index];
                    if (item !== undefined) state.onCommit(item);
                }}
                onHover={state.onHoverIndex}
                foot={
                    state.asking
                        ? t`Enter asks · Esc keeps it as plain text`
                        : undefined
                }
            />
        </div>
    );
};

// The `/` slash-TRIGGER's live state (open/dismissed while typing, tracked
// per element) — distinct from `SlashMenuState` above, which is the already-
// resolved POPUP (items/activeIndex/variant/onCommit) the host builds once
// this fires "open". Controlled, like every other interaction slot here: the
// host owns the state machine (inputRules.ts's `nextSlashTrigger`), this
// prop only names which element it currently applies to.
export type SlashTrigger = {
    readonly forElementId: ElementId;
    readonly state: SlashTriggerState;
};

// Everything the shell knows about one element beyond its content. Absent ⇒
// human-authored, nothing pending, no wear.
export type ElementAnnotation = {
    readonly authoredBy?: Provenance;
    readonly proposal?: PendingProposal;
    readonly markers?: readonly GutterMarker[];
};

export type MaterialDocumentProps = {
    readonly elements: readonly Element[];
    // The pluggable block renderers — the extensible seam.
    readonly renderers: ElementRenderers;
    // Per-element wear/proposal/provenance, keyed by id.
    readonly annotations?: ReadonlyMap<ElementId, ElementAnnotation>;
    // Which element's review surface is open (at most one at a time here).
    readonly reviewOpenFor?: ElementId;
    // Which element should show the adaptive over-trust cue.
    readonly overTrustFor?: ElementId;
    readonly onToggleReview?: (id: ElementId) => void;
    readonly onReview?: (id: ElementId, outcome: ReviewOutcome) => void;
    // A manual inline text edit at an element (or a choice), committed UP with
    // the target's id + its new text. Absent ⇒ the document is read-only.
    readonly onEditText?: (id: ElementId, text: string) => void;
    // A manual per-choice feedback edit (MCQ), committed UP with the choice id +
    // new feedback. Absent ⇒ feedback is read-only (shown only when present).
    readonly onEditFeedback?: (choiceId: ElementId, feedback: string) => void;
    // A field's text while it is still being typed (saved on a pause).
    readonly onDraftText?: (id: ElementId, text: string) => void;
    readonly onDraftFeedback?: (choiceId: ElementId, feedback: string) => void;
    // Mark / unmark a choice correct (MCQ). Present ⇒ each choice wires an
    // interactive correctness control; absent ⇒ the "Correct" badge is read-only.
    readonly onMarkChoiceCorrect?: (choiceId: ElementId) => void;
    readonly onUnmarkChoiceCorrect?: (choiceId: ElementId) => void;
    // Choice structure ops (MCQ). Present ⇒ each MCQ wires add / remove / reorder
    // affordances on its choices; absent ⇒ the choice list is fixed.
    readonly onAddChoice?: (mcqId: ElementId, after: KeptLine) => void;
    readonly onEndQuestion?: (mcqId: ElementId, end: QuestionEnd) => void;
    readonly onRemoveChoice?: (mcqId: ElementId, choiceId: ElementId) => void;
    readonly onMoveChoice?: (
        mcqId: ElementId,
        choiceId: ElementId,
        toIndex: number,
    ) => void;
    // Answer-mode switch (MCQ) — two intents. Present ⇒ a segmented control.
    readonly onAllowSingleAnswer?: (mcqId: ElementId) => void;
    readonly onAllowMultipleAnswers?: (mcqId: ElementId) => void;
    // Heading level (heading). Present (with `onInputRule`) ⇒ the block
    // menu's Turn into Heading / Subheading.
    readonly onSetHeadingLevel?: (
        id: ElementId,
        level: HeadingElement["level"],
    ) => void;
    // Structural block ops on the element array itself. Present ⇒ each block wires
    // a focus-revealed gutter grip: `onMoveElement` reorders to `toIndex` (index
    // in the list with the block removed — the shell computes up/down from the
    // block's position), `onRemoveElement` deletes it. Absent ⇒ blocks are fixed.
    readonly onMoveElement?: (id: ElementId, toIndex: number) => void;
    readonly onRemoveElement?: (id: ElementId) => void;
    readonly onMarkerActivate?: (id: ElementId, marker: GutterMarker) => void;
    readonly onJumpToSource?: (id: ElementId, anchorIndex: number) => void;
    // The `/` insert menu at one block, fully controlled — see SlashMenuState
    // above. Absent ⇒ no menu renders, ever (progressive disclosure default).
    readonly slashMenu?: SlashMenuState;
    // A markdown input-rule (`# `/etc) matched while typing a PROSE block's
    // text — committed UP with the element id + the already-built effect
    // (see ElementRendererProps.onInputRule in types.ts). Absent ⇒ prose
    // blocks are typed verbatim, never markdown-interpreted.
    readonly onInputRule?: (id: ElementId, effect: RuleEffect) => void;
    // The `/` slash-TRIGGER's live state — see SlashTrigger above. Absent ⇒
    // no element is tracked (every prose block reads "closed"). Distinct
    // from `slashMenu`: this drives the DETECTION inside a prose block's own
    // EditableText; `slashMenu` is the resulting POPUP once the host decides
    // to open one for the tracked element.
    readonly slashTrigger?: SlashTrigger;
    readonly onSlashTrigger?: (
        id: ElementId,
        state: SlashTriggerState,
        query: string,
    ) => void;
    // Keyboard-drive the open slash menu — ↑/↓ move the host's active row,
    // Enter commits it. Not per-element: the host already knows which block the
    // menu is for (`slashTrigger.forElementId`). Absent ⇒ slash menu is mouse-only.
    readonly onSlashNavigate?: (direction: "up" | "down") => void;
    readonly onSlashCommitActive?: () => boolean;
    // Document-first block flow — Enter at end of a top-level text block inserts
    // a new prose block below (the committed text threaded so the host persists
    // this block's edit + the insert as one chain); Backspace on an empty block
    // deletes it and focuses the previous. Keyed by the element the gesture fired
    // in. Absent ⇒ Enter/Backspace edit text as normal.
    readonly onEnterAtEnd?: (id: ElementId, committedText: string) => void;
    readonly onBackspaceEmpty?: (id: ElementId) => void;
    // A paste past a block's text ceiling (ElementRendererProps).
    readonly onPasteTooLong?: (text: string) => void;
    // Which element's primary EditableText should auto-enter edit mode +
    // take focus the moment it next mounts (Bug 1 fix, 2026-08-19) — see
    // `ElementRendererProps.autoEdit` in types.ts for the full mechanics.
    // Absent ⇒ no element auto-edits (every block reads `autoEdit={false}`).
    readonly autoEditElementId?: ElementId;
    // Fired once the targeted element's EditableText has consumed the
    // signal above, so the host can clear it (its own one-shot signal).
    readonly onAutoEditConsumed?: () => void;
    // "Open at this element": scroll the target's block into view + highlight
    // it briefly (revealElement.ts). `pathById` resolves a container target (a
    // section) to its first rendered block. Absent ⇒ no reveal.
    readonly revealTarget?: FocusTarget;
    readonly pathById?: ReadonlyMap<ElementId, readonly string[]>;
    // A part of a course or module among the text (its include node), drawn
    // by the surface around the editor (the Studio's tile). Absent: parts
    // show nothing here (the surface lists them elsewhere).
    readonly renderPart?: (part: PartElement) => ReactNode;
    // What stands before the first block, and follows the last (the Studio's
    // line above a leading part, and its writing line).
    readonly start?: ReactNode;
    readonly end?: ReactNode;
    // Names the document region for AT (caller-owned / localised).
    readonly ariaLabel: string;
    readonly className?: string;
};

const HUMAN: Provenance = { type: "human" };

export const MaterialDocument = ({
    elements,
    renderers,
    annotations,
    reviewOpenFor,
    overTrustFor,
    onToggleReview,
    onReview,
    onEditText,
    onEditFeedback,
    onDraftText,
    onDraftFeedback,
    onMarkChoiceCorrect,
    onUnmarkChoiceCorrect,
    onAddChoice,
    onEndQuestion,
    onRemoveChoice,
    onMoveChoice,
    onAllowSingleAnswer,
    onAllowMultipleAnswers,
    onSetHeadingLevel,
    onMoveElement,
    onRemoveElement,
    onMarkerActivate,
    onJumpToSource,
    slashMenu,
    onInputRule,
    slashTrigger,
    onSlashTrigger,
    onSlashNavigate,
    onSlashCommitActive,
    onEnterAtEnd,
    onBackspaceEmpty,
    onPasteTooLong,
    autoEditElementId,
    onAutoEditConsumed,
    revealTarget,
    pathById,
    renderPart,
    start,
    end,
    ariaLabel,
    className,
}: MaterialDocumentProps) => {
    const root = useRef<HTMLElement>(null);
    const landing = useLandingMarks();
    useRevealTarget(root, elements, pathById, revealTarget);
    // Total, type-narrowing dispatch: each case hands the renderer its exact
    // element variant plus the pending proposal (the renderer draws field-grain
    // diffs; block-grain ops are the ElementBlock shell's job) and the manual
    // edit callback (the renderer wires an EditableText affordance to it).
    //
    // Dispatches via JSX component invocation (`<Renderer .../>`), NOT a plain
    // function call (`renderers.heading({...})`) — that distinction is load-
    // bearing (Bug 2 fix, 2026-08-19). A plain call executes INSIDE
    // MaterialDocument's own render, so any hook a renderer calls (every one
    // of them calls `useLingui()`) was being counted onto MaterialDocument's
    // OWN hook list — whose length then tracked `elements.length`, tripping
    // React's "change in order of Hooks" the moment a block was inserted or
    // retyped. JSX invocation gives each per-kind renderer its OWN Fiber, so
    // its hooks are its own — stable across every render, however many
    // blocks exist or however their kinds change elsewhere in the document.
    const renderBody = (
        element: Element,
        proposal?: PendingProposal,
    ): ReactNode => {
        const isAutoEdit = autoEditElementId === element.id;
        // Only forwarded to the ONE targeted element; every other element's
        // renderer gets `autoEdit={false}`/`onAutoEditConsumed={undefined}`.
        const autoEditProps = {
            autoEdit: isAutoEdit,
            onAutoEditConsumed: isAutoEdit ? onAutoEditConsumed : undefined,
        };
        // Document-first block flow, bound to THIS element's id (the renderer's
        // own EditableText carries only the caret facts, not the id). Shared by
        // the three top-level text blocks (heading / prose / objective).
        const blockFlowProps = {
            onEnterAtEnd: onEnterAtEnd
                ? (text: string) => onEnterAtEnd(element.id, text)
                : undefined,
            onBackspaceEmpty: onBackspaceEmpty
                ? () => onBackspaceEmpty(element.id)
                : undefined,
            onPasteTooLong,
        };
        switch (element.type) {
            case "heading": {
                const Renderer = renderers.heading;
                return (
                    <Renderer
                        element={element}
                        proposal={proposal}
                        onEditText={onEditText}
                        onDraftText={onDraftText}
                        {...blockFlowProps}
                        {...autoEditProps}
                    />
                );
            }
            case "prose": {
                // Ask here: the line itself takes the request.
                if (
                    slashMenu?.type === "asking" &&
                    slashMenu.forElementId === element.id
                )
                    return (
                        <AskLine
                            words={slashMenu.words}
                            online={slashMenu.online}
                            max={slashMenu.max}
                            className="bilda-prose"
                            onAsk={slashMenu.onAsk}
                            onLeave={slashMenu.onLeave}
                        />
                    );
                const Renderer = renderers.prose;
                return (
                    <Renderer
                        element={element}
                        proposal={proposal}
                        onEditText={onEditText}
                        onDraftText={onDraftText}
                        onInputRule={
                            onInputRule
                                ? (effect) => onInputRule(element.id, effect)
                                : undefined
                        }
                        slashTriggerState={
                            slashTrigger?.forElementId === element.id
                                ? slashTrigger.state
                                : undefined
                        }
                        onSlashTrigger={
                            onSlashTrigger
                                ? (state, query) =>
                                      onSlashTrigger(element.id, state, query)
                                : undefined
                        }
                        onSlashNavigate={onSlashNavigate}
                        onSlashCommitActive={onSlashCommitActive}
                        {...blockFlowProps}
                        {...autoEditProps}
                    />
                );
            }
            case "objective": {
                const Renderer = renderers.objective;
                return (
                    <Renderer
                        element={element}
                        proposal={proposal}
                        onEditText={onEditText}
                        onDraftText={onDraftText}
                        {...blockFlowProps}
                        {...autoEditProps}
                    />
                );
            }
            case "note": {
                const Renderer = renderers.note;
                return (
                    <Renderer
                        element={element}
                        proposal={proposal}
                        onEditText={onEditText}
                        onDraftText={onDraftText}
                        {...blockFlowProps}
                        {...autoEditProps}
                    />
                );
            }
            case "mcq": {
                const Renderer = renderers.mcq;
                return (
                    <Renderer
                        element={element}
                        proposal={proposal}
                        onEditText={onEditText}
                        onDraftText={onDraftText}
                        onEditFeedback={onEditFeedback}
                        onDraftFeedback={onDraftFeedback}
                        onMarkChoiceCorrect={onMarkChoiceCorrect}
                        onUnmarkChoiceCorrect={onUnmarkChoiceCorrect}
                        onAddChoice={onAddChoice}
                        onEndQuestion={onEndQuestion}
                        ordinal={
                            elements
                                .slice(0, elements.indexOf(element) + 1)
                                .filter((e) => e.type === "mcq").length
                        }
                        {...autoEditProps}
                        onRemoveChoice={onRemoveChoice}
                        onMoveChoice={onMoveChoice}
                        onAllowSingleAnswer={onAllowSingleAnswer}
                        onAllowMultipleAnswers={onAllowMultipleAnswers}
                    />
                );
            }
            // Drawn by renderBlock, never as a block body.
            case "part":
                return null;
            default: {
                const exhaustive: never = element;
                return exhaustive;
            }
        }
    };

    // Copy/cut a selection as clean Markdown instead of the browser's raw
    // HTML→text flattening (the "Notion does this badly" bar the feature is
    // measured against), for two cases:
    //
    // 1. TWO OR MORE blocks: block-GRANULARITY, a stated first slice — the
    //    clipboard gets the Markdown for every WHOLE block the selection
    //    touches (start block through end block, inclusive), not a partial
    //    extract of text within the end blocks.
    // 2. ONE block, selected WHOLE (see `wholeBlockMarkdown`'s header note
    //    for the exact test): the clipboard gets that block's own Markdown
    //    source, so `*emphasis*`/`` `code` `` survive instead of being
    //    flattened by the browser reading the RENDERED HTML.
    //
    // SHORTCUT (stated per the lifecycle-design principle, not hidden): a
    // PARTIAL selection within one block (a few words of a paragraph) is
    // left to the browser's native copy. Mapping a rendered-HTML DOM
    // Range's offsets back to a precise position in that block's Markdown
    // SOURCE is a genuinely harder problem — Prose parses markdown to real
    // elements, so rendered HTML and source diverge — logged here as the
    // next increment rather than papered over with an approximation that
    // could silently truncate or mis-map the copied text.
    const onCopySelection = (event: ClipboardEvent<HTMLElement>) => {
        const selection = window.getSelection();
        if (selection === null || selection.isCollapsed) return;
        const startNode = nearestBlockElement(selection.anchorNode);
        const endNode = nearestBlockElement(selection.focusNode);
        if (startNode === undefined || endNode === undefined) return;
        const startId = startNode.dataset.elementId;
        const endId = endNode.dataset.elementId;
        if (startId === undefined || endId === undefined) return;

        if (startId === endId) {
            const element = elements.find((e) => e.id === startId);
            const bodyNode =
                startNode.querySelector<HTMLElement>("[data-block-body]");
            if (element === undefined || bodyNode === null) return;
            const markdown = wholeBlockMarkdown(
                element,
                selection.toString(),
                bodyNode.textContent ?? "",
            );
            // `undefined` ⇒ a partial in-block selection — fall through to
            // the browser's native copy (no preventDefault, no setData).
            if (markdown === undefined) return;
            event.clipboardData.setData("text/plain", markdown);
            event.preventDefault();
            return;
        }

        const startIndex = elements.findIndex((e) => e.id === startId);
        const endIndex = elements.findIndex((e) => e.id === endId);
        if (startIndex === -1 || endIndex === -1) return;
        const [lo, hi] =
            startIndex <= endIndex
                ? [startIndex, endIndex]
                : [endIndex, startIndex];
        const markdown = elementsToMarkdown(elements.slice(lo, hi + 1));
        event.clipboardData.setData("text/plain", markdown);
        event.preventDefault();
    };

    // The block menu's Turn into (pen Q76): a text block becomes a Heading,
    // a Subheading or Text through the existing doors, a new level
    // (`onSetHeadingLevel`) or a retype in place (`onInputRule`'s retype).
    // Absent: not a text block, or a host that does not offer both.
    const turnIntoFor = (element: Element) => {
        const current = textKindOf(element);
        if (
            current === undefined ||
            onSetHeadingLevel === undefined ||
            onInputRule === undefined
        )
            return undefined;
        const onTurn = (to: TextKind) => {
            const how = turnIntoOf(element, to);
            switch (how.type) {
                case "none":
                    return;
                case "level":
                    onSetHeadingLevel(element.id, how.level);
                    return;
                case "retype":
                    onInputRule(element.id, {
                        type: "retype",
                        command: {
                            type: "retype-element",
                            id: element.id,
                            to: how.to,
                        },
                    });
                    return;
                default:
                    return exhaustSwitchCase(how);
            }
        };
        return { current, onTurn };
    };

    const renderBlock = (element: Element, index: number): ReactNode => {
        // A part is an object among the text, with its own margin.
        if (element.type === "part")
            return (
                <div
                    className="bilda-material-document__part"
                    key={element.id}
                    data-element-id={element.id}
                >
                    {renderPart?.(element) ?? null}
                </div>
            );
        const a = annotations?.get(element.id);
        // How it lands (Studio, while its panel is open): the block's mark.
        const lands = landing?.blocks.get(element.id);
        const markers: readonly GutterMarker[] = [
            ...(a?.markers ?? []),
            ...(lands === undefined
                ? []
                : [{ type: "landing", mark: lands } satisfies GutterMarker]),
        ];
        // Up/down reorder in the block gutter: a block moves to `index-1`
        // (up) or `index+1` (down) — both indices into the list with the
        // block removed, the `move-element` toIndex contract. Offered only
        // where the move is legal (not up from the top, not down from the
        // bottom), so the affordance never proposes an impossible move.
        const canMoveUp = onMoveElement !== undefined && index > 0;
        const canMoveDown =
            onMoveElement !== undefined && index < elements.length - 1;
        return (
            <div
                className="bilda-material-document__block"
                key={element.id}
                // Names this wrapper as element.id's block for the
                // copy/cut handler's `blockIdOf` walk above and the
                // reveal effect's lookup (revealElement.ts).
                data-element-id={element.id}
                data-landing-pointed={
                    landing?.pointed.has(element.id) ? "" : undefined
                }
            >
                <ElementBlock
                    body={renderBody(element, a?.proposal)}
                    authoredBy={a?.authoredBy ?? HUMAN}
                    proposal={a?.proposal}
                    markers={markers}
                    reviewOpen={reviewOpenFor === element.id}
                    overTrustCue={overTrustFor === element.id}
                    onToggleReview={() => onToggleReview?.(element.id)}
                    onReview={(o) => onReview?.(element.id, o)}
                    onMarkerActivate={(m) =>
                        m.type === "landing"
                            ? landing?.onPoint([element.id])
                            : onMarkerActivate?.(element.id, m)
                    }
                    onJumpToSource={(i) => onJumpToSource?.(element.id, i)}
                    onMoveUp={
                        canMoveUp
                            ? () => onMoveElement?.(element.id, index - 1)
                            : undefined
                    }
                    onMoveDown={
                        canMoveDown
                            ? () => onMoveElement?.(element.id, index + 1)
                            : undefined
                    }
                    onRemove={
                        onRemoveElement !== undefined
                            ? () => onRemoveElement(element.id)
                            : undefined
                    }
                    turnInto={turnIntoFor(element)}
                />
                {slashMenu?.forElementId === element.id ? (
                    <SlashSlot state={slashMenu} />
                ) : null}
            </div>
        );
    };

    return (
        <article
            ref={root}
            aria-label={ariaLabel}
            className={`bilda-material-document${
                className ? ` ${className}` : ""
            }`}
            onCopy={onCopySelection}
            onCut={onCopySelection}
        >
            {start}
            {/* Consecutive same-kind blocks form a run; a kind the registry
                frames (objectives → one compact list) is wrapped as one. */}
            {elementRuns(elements).map((run) => {
                const blocks = run.items.map(({ element, index }) =>
                    renderBlock(element, index),
                );
                // A component (JSX), never a plain call — a frame may use
                // hooks (see the renderBody note on the same distinction).
                const Frame = renderers.runs?.[run.kind];
                const key = run.items[0]?.element.id ?? run.kind;
                return Frame === undefined ? (
                    <Fragment key={key}>{blocks}</Fragment>
                ) : (
                    <Frame key={key} count={blocks.length}>
                        {blocks}
                    </Frame>
                );
            })}
            {end}
        </article>
    );
};
